"use client";

import { c as _c } from "react/compiler-runtime";
import { Clock, MapPin, Search, SlidersHorizontal, X } from "lucide-react";
import { useSearchParams } from "next/navigation";
import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { OutOfAreaNotice } from "~/app/(site)/_components/OutOfAreaNotice";
import { DateTimeFields } from "~/app/(site)/search/_components/DateTimeFields";
import { getActiveFilterCount } from "~/app/(site)/search/_components/FiltersSheet";
import { type VehicleSize, VehicleSizePicker } from "~/app/(site)/search/_components/VehicleSizePicker";
import { refreshNowSlot, useSearchDraft } from "~/hooks/useSearchWindow";
import { safeUUID } from "~/lib/auth/uuid";
import { STALE_WINDOW_MESSAGE } from "~/lib/constants/search";
import { type PlaceSuggestion, retrievePlace, suggestPlaces } from "~/lib/mapbox/mapbox";
import { isWithinServiceArea } from "~/lib/service-area";
import { cn } from "~/lib/utils";
import { formatShort } from "~/lib/utils/datetime";
import { useSearchDraftStore } from "~/store/searchDraftStore";
import { useSearchUIStore } from "~/store/searchUIStore";
type ActiveSection = "location" | "start" | "end" | "vehicle" | null;

// Both pill instances are mounted at once, but only one is ever visible (the
// other is hidden by CSS and force-closed), so a single id per panel is unique
// among rendered nodes.
const LOCATION_PANEL_ID = "search-pill-location-panel";
const DATE_PANEL_ID = "search-pill-date-panel";
const VEHICLE_PANEL_ID = "search-pill-vehicle-panel";
const LOCATION_HINT_ID = "search-pill-location-hint";
const LOCATION_ERROR_ID = "search-pill-location-error";

/**
 * Whether an event landed inside a Radix layer that renders outside the panel's
 * DOM subtree and dismisses itself.
 *
 * Radix listens for Escape in the capture phase and never calls
 * `preventDefault`, so neither `contains` nor `defaultPrevented` can tell us it
 * already handled the key — matching its markers is the only reliable signal.
 * Without this, one Escape closes the time dropdown *and* the panel behind it.
 *
 * Deliberately not including the calendar: it is an inline div that does not
 * dismiss itself, so Escape there should still close the panel.
 */
function isInsideSelfDismissingLayer(target: Element | null): boolean {
  if (!target?.closest) return false;
  return !!(target.closest("[data-slot='select-content']") ?? target.closest("[data-radix-select-viewport]") ?? target.closest("[data-radix-popper-content-wrapper]"));
}

/**
 * Nearly clear directly under the pill and greying toward the edges, so the eye
 * settles on the search bar rather than the page behind it. Centred at the top,
 * which is where the pill sits.
 */
const SCRIM_BACKGROUND = ["radial-gradient(120% 90% at 50% 0%,", "color-mix(in srgb, var(--ink) 2%, transparent) 0%,", "color-mix(in srgb, var(--ink) 10%, transparent) 52%,", "color-mix(in srgb, var(--ink) 20%, transparent) 100%)"].join(" ");
const VEHICLE_LABELS: Record<VehicleSize, string> = {
  motorcycle: "Motorcycle",
  subcompact: "Subcompact",
  compact: "Compact",
  standard: "Standard",
  suv: "SUV",
  truck: "Truck",
  oversized: "Oversized"
};
interface SearchNavPillProps {
  onFiltersOpen?: () => void;
  /** Pass false when this instance is hidden by CSS — closes any open panel */
  isVisible?: boolean;
}
export function SearchNavPill(t0) {
  const $ = _c(147);
  const {
    onFiltersOpen,
    isVisible: t1
  } = t0;
  const isVisible = t1 === undefined ? true : t1;
  const rawParams = useSearchParams();
  const activeFilterCount = getActiveFilterCount(rawParams);
  const {
    q: location,
    place: selectedPlace,
    starts_at: startsAt,
    ends_at: endsAt,
    vehicleSize,
    now,
    timeZone: destTimeZone,
    searchUrl,
    setLocation,
    setPlace,
    setStartsAt,
    setEndsAt,
    setVehicleSize,
    hasPendingChanges,
    outOfArea,
    commitSearch,
    revertToCommittedLocation,
    reconcileWindow,
    reseedFromUrl
  } = useSearchDraft();
  const [activeSection, setActiveSection] = useState(null);
  const staleWindow = useSearchUIStore(_temp);
  const showStaleHint = staleWindow && !activeSection;
  const activeSectionRef = useRef(null);
  let t2;
  let t3;
  if ($[0] !== activeSection) {
    t2 = () => {
      activeSectionRef.current = activeSection;
    };
    t3 = [activeSection];
    $[0] = activeSection;
    $[1] = t2;
    $[2] = t3;
  } else {
    t2 = $[1];
    t3 = $[2];
  }
  useEffect(t2, t3);
  let t4;
  let t5;
  if ($[3] !== isVisible) {
    t4 = () => {
      if (!isVisible) {
        setActiveSection(null);
      }
    };
    t5 = [isVisible];
    $[3] = isVisible;
    $[4] = t4;
    $[5] = t5;
  } else {
    t4 = $[4];
    t5 = $[5];
  }
  useEffect(t4, t5);
  let t6;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = [];
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  const [suggestions, setSuggestions] = useState(t6);
  const [isLoading, setIsLoading] = useState(false);
  const [retrieveFailed, setRetrieveFailed] = useState(false);
  const [sessionToken] = useState(_temp2);
  const debounceRef = useRef(null);
  const suggestRequestRef = useRef(0);
  const pillRef = useRef(null);
  const dropdownRef = useRef(null);
  const locationInputRef = useRef(null);
  const locationBtnRef = useRef(null);
  const startSectionRef = useRef(null);
  const vehicleBtnRef = useRef(null);
  let t7;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = () => {
      suggestRequestRef.current = suggestRequestRef.current + 1;
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
      debounceRef.current = null;
      setSuggestions([]);
      setIsLoading(false);
      setRetrieveFailed(false);
    };
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  const clearSuggestions = t7;
  let t8;
  if ($[8] !== revertToCommittedLocation) {
    t8 = t9 => {
      const restoreFocus = t9 === undefined ? false : t9;
      const previous = activeSectionRef.current;
      setActiveSection(null);
      clearSuggestions();
      revertToCommittedLocation();
      if (!restoreFocus || !previous) {
        return;
      }
      const trigger = previous === "location" ? locationBtnRef.current : previous === "vehicle" ? vehicleBtnRef.current : startSectionRef.current;
      trigger?.focus();
    };
    $[8] = revertToCommittedLocation;
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  const dismiss = t8;
  let t9;
  if ($[10] !== commitSearch || $[11] !== dismiss || $[12] !== reseedFromUrl) {
    t9 = t10 => {
      const restoreFocus_0 = t10 === undefined ? false : t10;
      dismiss(restoreFocus_0);
      const {
        place
      } = useSearchDraftStore.getState();
      if (place && !isWithinServiceArea(place.lat, place.lng)) {
        reseedFromUrl();
      } else {
        commitSearch();
      }
    };
    $[10] = commitSearch;
    $[11] = dismiss;
    $[12] = reseedFromUrl;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  const close = t9;
  let t10;
  if ($[14] !== dismiss || $[15] !== reseedFromUrl) {
    t10 = () => {
      reseedFromUrl();
      dismiss(true);
    };
    $[14] = dismiss;
    $[15] = reseedFromUrl;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  const cancel = t10;
  let t11;
  let t12;
  if ($[17] !== activeSection) {
    t11 = () => {
      if (!activeSection) {
        return;
      }
      if (activeSection === "location") {
        locationInputRef.current?.focus();
      } else {
        dropdownRef.current?.querySelector("button")?.focus();
      }
    };
    t12 = [activeSection];
    $[17] = activeSection;
    $[18] = t11;
    $[19] = t12;
  } else {
    t11 = $[18];
    t12 = $[19];
  }
  useEffect(t11, t12);
  let t13;
  let t14;
  if ($[20] !== activeSection || $[21] !== close || $[22] !== dismiss) {
    t13 = () => {
      if (!activeSection) {
        return;
      }
      const handler = e => {
        const t = e.target as Element;
        if (!pillRef.current?.contains(t as Node) && !dropdownRef.current?.contains(t as Node) && !isInsideSelfDismissingLayer(t) && !t.closest?.("[data-ezspot-calendar]")) {
          if (t.closest?.("a[href]")) {
            dismiss();
          } else {
            close();
          }
        }
      };
      document.addEventListener("mousedown", handler, true);
      return () => document.removeEventListener("mousedown", handler, true);
    };
    t14 = [activeSection, close, dismiss];
    $[20] = activeSection;
    $[21] = close;
    $[22] = dismiss;
    $[23] = t13;
    $[24] = t14;
  } else {
    t13 = $[23];
    t14 = $[24];
  }
  useEffect(t13, t14);
  let t15;
  let t16;
  if ($[25] !== activeSection || $[26] !== cancel) {
    t15 = () => {
      if (!activeSection) {
        return;
      }
      const handler_0 = e_0 => {
        if (e_0.key !== "Escape") {
          return;
        }
        if (isInsideSelfDismissingLayer(e_0.target as Element)) {
          return;
        }
        cancel();
      };
      document.addEventListener("keydown", handler_0);
      return () => document.removeEventListener("keydown", handler_0);
    };
    t16 = [activeSection, cancel];
    $[25] = activeSection;
    $[26] = cancel;
    $[27] = t15;
    $[28] = t16;
  } else {
    t15 = $[27];
    t16 = $[28];
  }
  useEffect(t15, t16);
  let t17;
  if ($[29] !== sessionToken || $[30] !== setLocation) {
    t17 = value => {
      setLocation(value);
      clearSuggestions();
      if (value.trim().length < 2) {
        return;
      }
      const requestId = suggestRequestRef.current;
      debounceRef.current = setTimeout(async () => {
        setIsLoading(true);
        const results = await suggestPlaces(value, sessionToken);
        if (requestId !== suggestRequestRef.current) {
          return;
        }
        setIsLoading(false);
        setSuggestions(results);
      }, 300);
    };
    $[29] = sessionToken;
    $[30] = setLocation;
    $[31] = t17;
  } else {
    t17 = $[31];
  }
  const handleLocationInput = t17;
  const handleSelectSuggestion = async suggestion => {
    setSuggestions([]);
    setRetrieveFailed(false);
    setIsLoading(true);
    const place_0 = await retrievePlace(suggestion.mapbox_id, sessionToken);
    setIsLoading(false);
    if (!place_0) {
      setRetrieveFailed(true);
      locationInputRef.current?.focus();
      return;
    }
    setLocation(place_0.label || suggestion.name);
    setPlace(place_0);
    if (!isWithinServiceArea(place_0.lat, place_0.lng)) {
      locationInputRef.current?.focus();
      return;
    }
    close(true);
  };
  const canSearch = !!searchUrl && !!selectedPlace && !outOfArea && hasPendingChanges;
  let t18;
  if ($[32] !== canSearch || $[33] !== close) {
    t18 = () => {
      if (!canSearch) {
        return;
      }
      close();
    };
    $[32] = canSearch;
    $[33] = close;
    $[34] = t18;
  } else {
    t18 = $[34];
  }
  const handleSearch = t18;
  let t19;
  if ($[35] !== location || $[36] !== selectedPlace) {
    t19 = location.trim().length > 0 && !selectedPlace;
    $[35] = location;
    $[36] = selectedPlace;
    $[37] = t19;
  } else {
    t19 = $[37];
  }
  const needsPlace = t19;
  const locationDisplay = location || null;
  let t20;
  if ($[38] !== startsAt) {
    t20 = startsAt ? formatShort(startsAt) : null;
    $[38] = startsAt;
    $[39] = t20;
  } else {
    t20 = $[39];
  }
  const startDisplay = t20;
  let t21;
  if ($[40] !== endsAt) {
    t21 = endsAt ? formatShort(endsAt) : null;
    $[40] = endsAt;
    $[41] = t21;
  } else {
    t21 = $[41];
  }
  const endDisplay = t21;
  const vehicleDisplay = vehicleSize ? VEHICLE_LABELS[vehicleSize] : null;
  const [dropdownPos, setDropdownPos] = useState(null);
  let t22;
  if ($[42] !== reconcileWindow) {
    t22 = function openDateSection(initialCalendar) {
      refreshNowSlot();
      reconcileWindow();
      const rect = startSectionRef.current?.getBoundingClientRect();
      if (rect) {
        const left = Math.max(8, Math.min(rect.left + window.scrollX, window.innerWidth - 380 - 8));
        setDropdownPos({
          top: rect.bottom + window.scrollY + 8,
          left,
          width: 380
        });
      }
      setActiveSection(initialCalendar === "start" ? "start" : "end");
    };
    $[42] = reconcileWindow;
    $[43] = t22;
  } else {
    t22 = $[43];
  }
  const openDateSection = t22;
  let t23;
  if ($[44] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = function openVehicleSection() {
      const pillRect = pillRef.current?.getBoundingClientRect();
      if (pillRect) {
        const left_0 = Math.max(8, Math.min(pillRect.right + window.scrollX - 340, window.innerWidth - 340 - 8));
        setDropdownPos({
          top: pillRect.bottom + window.scrollY + 8,
          left: left_0,
          width: 340
        });
      }
      setActiveSection("vehicle");
    };
    $[44] = t23;
  } else {
    t23 = $[44];
  }
  const openVehicleSection = t23;
  let t24;
  if ($[45] !== activeSection || $[46] !== close) {
    t24 = function openLocationSection() {
      if (activeSection === "location") {
        close();
        return;
      }
      const pillRect_0 = pillRef.current?.getBoundingClientRect();
      if (pillRect_0) {
        const left_1 = Math.max(8, Math.min(pillRect_0.left + (pillRect_0.width - 420) / 2 + window.scrollX, window.innerWidth - 420 - 8));
        setDropdownPos({
          top: pillRect_0.bottom + window.scrollY + 8,
          left: left_1,
          width: 420
        });
      }
      setActiveSection("location");
    };
    $[45] = activeSection;
    $[46] = close;
    $[47] = t24;
  } else {
    t24 = $[47];
  }
  const openLocationSection = t24;
  let t25;
  if ($[48] !== activeSection) {
    t25 = activeSection && typeof document !== "undefined" && createPortal(<div aria-hidden={true} className="fixed inset-0 z-40 hidden md:block" style={{
      background: SCRIM_BACKGROUND
    }} />, document.body);
    $[48] = activeSection;
    $[49] = t25;
  } else {
    t25 = $[49];
  }
  const scrim = t25;
  const locationPanel = activeSection === "location" && dropdownPos && typeof document !== "undefined" && createPortal(<div ref={dropdownRef} id={LOCATION_PANEL_ID} role="dialog" aria-label="Edit location" className="rounded-3xl border border-[var(--ink-10)] bg-white p-4" style={{
    position: "absolute",
    top: dropdownPos.top,
    left: dropdownPos.left,
    zIndex: 9999,
    width: dropdownPos.width,
    boxShadow: "0 8px 32px rgba(0,0,0,0.12), 0 2px 8px rgba(0,0,0,0.06)"
  }}><div className="flex items-center gap-2 rounded-xl border border-[var(--ink-20)] bg-[var(--ink-05)] px-3 py-2.5"><MapPin className="h-4 w-4 shrink-0 text-[var(--ink-60)]" /><input type="text" value={location} onChange={e_1 => handleLocationInput(e_1.target.value)} placeholder="Search for a location..." className="w-full bg-transparent text-[14px] text-[var(--ink)] outline-none placeholder:text-[var(--ink-40)]" ref={locationInputRef} aria-describedby={retrieveFailed ? LOCATION_ERROR_ID : needsPlace ? LOCATION_HINT_ID : undefined} />{isLoading ? <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-[var(--ink-20)] border-t-[var(--blue)]" /> : location.length > 0 ? <button type="button" aria-label="Clear location" onMouseDown={_temp3} onClick={() => {
        setLocation("");
        clearSuggestions();
      }} className="flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded-full bg-[var(--ink-20)] transition-colors hover:bg-[var(--ink-40)]"><X className="h-2 w-2 text-black" strokeWidth={3} /></button> : null}</div>{outOfArea && <OutOfAreaNotice city={selectedPlace?.city} onChangeDestination={() => {
      setLocation("");
      clearSuggestions();
      locationInputRef.current?.focus();
    }} />}{retrieveFailed ? <p id={LOCATION_ERROR_ID} role="alert" className="mt-2 px-1 text-[12px] text-(--red-text)">Couldn't load that address. Try it again or pick another.</p> : needsPlace && suggestions.length === 0 && !isLoading && <p id={LOCATION_HINT_ID} className="mt-2 px-1 text-[12px] text-[var(--ink-60)]">Choose a suggested address to search.</p>}{suggestions.length > 0 && <ul className="mt-2 space-y-0.5">{suggestions.map(s_0 => <li key={s_0.mapbox_id}><button type="button" onMouseDown={e_3 => {
          e_3.preventDefault();
          handleSelectSuggestion(s_0);
        }} className="flex w-full items-start gap-2.5 rounded-xl px-3 py-2.5 text-left transition-colors hover:bg-[var(--ink-05)]"><MapPin className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[var(--blue)]" /><span className="min-w-0"><span className="block truncate text-[13px] font-medium text-[var(--ink)]">{s_0.name}</span>{s_0.place_formatted && <span className="block truncate text-[12px] text-[var(--ink-60)]">{s_0.place_formatted}</span>}</span></button></li>)}</ul>}</div>, document.body);
  let t26;
  if ($[50] !== activeSection || $[51] !== destTimeZone || $[52] !== dropdownPos || $[53] !== endsAt || $[54] !== now || $[55] !== setEndsAt || $[56] !== setStartsAt || $[57] !== startsAt) {
    t26 = (activeSection === "start" || activeSection === "end") && dropdownPos && typeof document !== "undefined" && createPortal(<div ref={dropdownRef} id={DATE_PANEL_ID} role="dialog" aria-label="Edit start and end time" className="rounded-2xl border border-[var(--ink-20)] bg-white p-4" style={{
      position: "absolute",
      top: dropdownPos.top,
      left: dropdownPos.left,
      zIndex: 9999,
      width: dropdownPos.width,
      boxShadow: "0 8px 32px rgba(0,0,0,0.12), 0 2px 8px rgba(0,0,0,0.06)"
    }}><DateTimeFields startsAt={startsAt} endsAt={endsAt} onStartsAtChange={setStartsAt} onEndsAtChange={setEndsAt} now={now} timeZone={destTimeZone} initialActiveCalendar={null} /></div>, document.body);
    $[50] = activeSection;
    $[51] = destTimeZone;
    $[52] = dropdownPos;
    $[53] = endsAt;
    $[54] = now;
    $[55] = setEndsAt;
    $[56] = setStartsAt;
    $[57] = startsAt;
    $[58] = t26;
  } else {
    t26 = $[58];
  }
  const datePanel = t26;
  let t27;
  if ($[59] !== activeSection || $[60] !== close || $[61] !== dropdownPos || $[62] !== setVehicleSize || $[63] !== vehicleSize) {
    t27 = activeSection === "vehicle" && dropdownPos && typeof document !== "undefined" && createPortal(<div ref={dropdownRef} id={VEHICLE_PANEL_ID} role="dialog" aria-label="Select vehicle size" className="rounded-2xl border border-[var(--ink-10)] bg-white p-5" style={{
      position: "absolute",
      top: dropdownPos.top,
      left: dropdownPos.left,
      zIndex: 9999,
      width: dropdownPos.width,
      boxShadow: "0 8px 32px rgba(0,0,0,0.12), 0 2px 8px rgba(0,0,0,0.06)"
    }}><VehicleSizePicker value={vehicleSize} onChange={size => {
        setVehicleSize(size);
        close(true);
      }} showLabel={false} /></div>, document.body);
    $[59] = activeSection;
    $[60] = close;
    $[61] = dropdownPos;
    $[62] = setVehicleSize;
    $[63] = vehicleSize;
    $[64] = t27;
  } else {
    t27 = $[64];
  }
  const vehiclePanel = t27;
  const t28 = activeSection === "location";
  const t29 = activeSection === "location" ? LOCATION_PANEL_ID : undefined;
  const t30 = activeSection === "location" ? "bg-[var(--ink-05)]" : "hover:bg-[var(--ink-05)]";
  let t31;
  if ($[65] !== t30) {
    t31 = cn("flex flex-col items-center px-5 py-2 rounded-[4px] first:rounded-l-full transition-colors min-w-[160px] max-w-[220px]", t30);
    $[65] = t30;
    $[66] = t31;
  } else {
    t31 = $[66];
  }
  let t32;
  if ($[67] === Symbol.for("react.memo_cache_sentinel")) {
    t32 = <span className="text-[10px] font-semibold text-[var(--ink)] uppercase tracking-wide">Location</span>;
    $[67] = t32;
  } else {
    t32 = $[67];
  }
  const t33 = locationDisplay ? "text-[var(--ink)] font-medium" : "text-[var(--ink-40)]";
  let t34;
  if ($[68] !== t33) {
    t34 = cn("text-[13px] truncate max-w-[180px]", t33);
    $[68] = t33;
    $[69] = t34;
  } else {
    t34 = $[69];
  }
  const t35 = locationDisplay ?? "Search location";
  let t36;
  if ($[70] !== t34 || $[71] !== t35) {
    t36 = <span className={t34}>{t35}</span>;
    $[70] = t34;
    $[71] = t35;
    $[72] = t36;
  } else {
    t36 = $[72];
  }
  let t37;
  if ($[73] !== openLocationSection || $[74] !== t28 || $[75] !== t29 || $[76] !== t31 || $[77] !== t36) {
    t37 = <button ref={locationBtnRef} type="button" onClick={openLocationSection} aria-haspopup="dialog" aria-expanded={t28} aria-controls={t29} className={t31}>{t32}{t36}</button>;
    $[73] = openLocationSection;
    $[74] = t28;
    $[75] = t29;
    $[76] = t31;
    $[77] = t36;
    $[78] = t37;
  } else {
    t37 = $[78];
  }
  let t38;
  if ($[79] === Symbol.for("react.memo_cache_sentinel")) {
    t38 = <div className="w-px h-6 bg-[var(--ink-20)] mx-0.5" />;
    $[79] = t38;
  } else {
    t38 = $[79];
  }
  let t39;
  if ($[80] !== openDateSection) {
    t39 = () => openDateSection("start");
    $[80] = openDateSection;
    $[81] = t39;
  } else {
    t39 = $[81];
  }
  const t40 = activeSection === "start" || activeSection === "end";
  const t41 = activeSection === "start" || activeSection === "end" ? DATE_PANEL_ID : undefined;
  const t42 = activeSection === "start" || activeSection === "end" ? "bg-[var(--ink-05)]" : "hover:bg-[var(--ink-05)]";
  let t43;
  if ($[82] !== t42) {
    t43 = cn("flex items-center gap-3 px-5 py-2 rounded-[4px] transition-colors", t42);
    $[82] = t42;
    $[83] = t43;
  } else {
    t43 = $[83];
  }
  let t44;
  if ($[84] === Symbol.for("react.memo_cache_sentinel")) {
    t44 = <span className="text-[10px] font-semibold text-[var(--ink)] uppercase tracking-wide">Start time</span>;
    $[84] = t44;
  } else {
    t44 = $[84];
  }
  const t45 = startDisplay ? "text-[var(--ink)] font-medium" : "text-[var(--ink-40)]";
  let t46;
  if ($[85] !== t45) {
    t46 = cn("text-[13px] whitespace-nowrap", t45);
    $[85] = t45;
    $[86] = t46;
  } else {
    t46 = $[86];
  }
  const t47 = startDisplay ?? "Add start";
  let t48;
  if ($[87] !== t46 || $[88] !== t47) {
    t48 = <div className="flex flex-col items-start">{t44}<span className={t46}>{t47}</span></div>;
    $[87] = t46;
    $[88] = t47;
    $[89] = t48;
  } else {
    t48 = $[89];
  }
  let t49;
  if ($[90] === Symbol.for("react.memo_cache_sentinel")) {
    t49 = <div className="w-px h-5 bg-[var(--ink-10)]" />;
    $[90] = t49;
  } else {
    t49 = $[90];
  }
  let t50;
  if ($[91] === Symbol.for("react.memo_cache_sentinel")) {
    t50 = <span className="text-[10px] font-semibold uppercase tracking-wide text-[var(--ink)]">End time</span>;
    $[91] = t50;
  } else {
    t50 = $[91];
  }
  const t51 = endDisplay ? "text-[var(--ink)] font-medium" : "text-[var(--ink-40)]";
  let t52;
  if ($[92] !== t51) {
    t52 = cn("text-[13px] whitespace-nowrap", t51);
    $[92] = t51;
    $[93] = t52;
  } else {
    t52 = $[93];
  }
  const t53 = endDisplay ?? "Add end";
  let t54;
  if ($[94] !== t52 || $[95] !== t53) {
    t54 = <div className="flex flex-col items-start">{t50}<span className={t52}>{t53}</span></div>;
    $[94] = t52;
    $[95] = t53;
    $[96] = t54;
  } else {
    t54 = $[96];
  }
  let t55;
  if ($[97] !== t39 || $[98] !== t40 || $[99] !== t41 || $[100] !== t43 || $[101] !== t48 || $[102] !== t54) {
    t55 = <button ref={startSectionRef} type="button" onClick={t39} aria-haspopup="dialog" aria-expanded={t40} aria-controls={t41} className={t43}>{t48}{t49}{t54}</button>;
    $[97] = t39;
    $[98] = t40;
    $[99] = t41;
    $[100] = t43;
    $[101] = t48;
    $[102] = t54;
    $[103] = t55;
  } else {
    t55 = $[103];
  }
  let t56;
  if ($[104] !== showStaleHint) {
    t56 = <StaleWindowHint show={showStaleHint} />;
    $[104] = showStaleHint;
    $[105] = t56;
  } else {
    t56 = $[105];
  }
  let t57;
  if ($[106] !== t55 || $[107] !== t56) {
    t57 = <div className="relative">{t55}{t56}</div>;
    $[106] = t55;
    $[107] = t56;
    $[108] = t57;
  } else {
    t57 = $[108];
  }
  let t58;
  if ($[109] === Symbol.for("react.memo_cache_sentinel")) {
    t58 = <div className="w-px h-6 bg-[var(--ink-20)] mx-0.5" />;
    $[109] = t58;
  } else {
    t58 = $[109];
  }
  const t59 = activeSection === "vehicle";
  const t60 = activeSection === "vehicle" ? VEHICLE_PANEL_ID : undefined;
  const t61 = activeSection === "vehicle" ? "bg-[var(--ink-05)]" : "hover:bg-[var(--ink-05)]";
  let t62;
  if ($[110] !== t61) {
    t62 = cn("flex flex-col items-center px-5 py-2 rounded-[4px] transition-colors", t61);
    $[110] = t61;
    $[111] = t62;
  } else {
    t62 = $[111];
  }
  let t63;
  if ($[112] === Symbol.for("react.memo_cache_sentinel")) {
    t63 = <span className="text-[10px] font-semibold text-[var(--ink)] uppercase tracking-wide">Vehicle</span>;
    $[112] = t63;
  } else {
    t63 = $[112];
  }
  const t64 = vehicleDisplay ? "text-[var(--ink)] font-medium" : "text-[var(--ink-40)]";
  let t65;
  if ($[113] !== t64) {
    t65 = cn("text-[13px] whitespace-nowrap", t64);
    $[113] = t64;
    $[114] = t65;
  } else {
    t65 = $[114];
  }
  const t66 = vehicleDisplay ?? "Select size";
  let t67;
  if ($[115] !== t65 || $[116] !== t66) {
    t67 = <span className={t65}>{t66}</span>;
    $[115] = t65;
    $[116] = t66;
    $[117] = t67;
  } else {
    t67 = $[117];
  }
  let t68;
  if ($[118] !== openVehicleSection || $[119] !== t59 || $[120] !== t60 || $[121] !== t62 || $[122] !== t67) {
    t68 = <button ref={vehicleBtnRef} type="button" onClick={openVehicleSection} aria-haspopup="dialog" aria-expanded={t59} aria-controls={t60} className={t62}>{t63}{t67}</button>;
    $[118] = openVehicleSection;
    $[119] = t59;
    $[120] = t60;
    $[121] = t62;
    $[122] = t67;
    $[123] = t68;
  } else {
    t68 = $[123];
  }
  const t69 = !canSearch;
  const t70 = canSearch ? "bg-[var(--blue)] text-white hover:opacity-90 shadow-sm" : "bg-[var(--blue)] text-white opacity-60";
  let t71;
  if ($[124] !== t70) {
    t71 = cn("flex h-10 w-10 items-center justify-center rounded-full transition-all", t70);
    $[124] = t70;
    $[125] = t71;
  } else {
    t71 = $[125];
  }
  let t72;
  if ($[126] === Symbol.for("react.memo_cache_sentinel")) {
    t72 = <Search className="h-4 w-4" strokeWidth={2.5} />;
    $[126] = t72;
  } else {
    t72 = $[126];
  }
  let t73;
  if ($[127] !== handleSearch || $[128] !== t69 || $[129] !== t71) {
    t73 = <div className="pr-2 pl-1"><button type="button" onClick={handleSearch} disabled={t69} className={t71} aria-label="Search">{t72}</button></div>;
    $[127] = handleSearch;
    $[128] = t69;
    $[129] = t71;
    $[130] = t73;
  } else {
    t73 = $[130];
  }
  let t74;
  if ($[131] !== activeFilterCount || $[132] !== close || $[133] !== onFiltersOpen) {
    t74 = onFiltersOpen && <><div className="mx-1 h-5 w-px bg-[var(--ink-20)]" /><button type="button" onClick={e_4 => {
        e_4.stopPropagation();
        close();
        onFiltersOpen();
      }} className={cn("relative mr-2 flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-[13px] font-medium transition-all", activeFilterCount > 0 ? "border-[var(--blue)] bg-[var(--blue-pale)] text-[var(--blue)]" : "border-[var(--ink-20)] text-[var(--ink-60)] hover:bg-[var(--ink-05)] hover:text-[var(--ink)] hover:border-[var(--ink-40)]")}><SlidersHorizontal className="h-3.5 w-3.5" strokeWidth={2.2} />Filters{activeFilterCount > 0 && <span className="flex h-4 w-4 items-center justify-center rounded-full bg-[var(--blue)] text-[9px] font-bold text-white">{activeFilterCount}</span>}</button></>;
    $[131] = activeFilterCount;
    $[132] = close;
    $[133] = onFiltersOpen;
    $[134] = t74;
  } else {
    t74 = $[134];
  }
  let t75;
  if ($[135] !== t37 || $[136] !== t57 || $[137] !== t68 || $[138] !== t73 || $[139] !== t74) {
    t75 = <div ref={pillRef} className="hidden md:flex items-center rounded-full border border-[var(--ink-20)] bg-white shadow-sm hover:shadow-md transition-shadow cursor-pointer py-1 px-1">{t37}{t38}{t57}{t58}{t68}{t73}{t74}</div>;
    $[135] = t37;
    $[136] = t57;
    $[137] = t68;
    $[138] = t73;
    $[139] = t74;
    $[140] = t75;
  } else {
    t75 = $[140];
  }
  let t76;
  if ($[141] !== datePanel || $[142] !== locationPanel || $[143] !== scrim || $[144] !== t75 || $[145] !== vehiclePanel) {
    t76 = <>{t75}{scrim}{locationPanel}{datePanel}{vehiclePanel}</>;
    $[141] = datePanel;
    $[142] = locationPanel;
    $[143] = scrim;
    $[144] = t75;
    $[145] = vehiclePanel;
    $[146] = t76;
  } else {
    t76 = $[146];
  }
  return t76;
}

/**
 * Stale-window tooltip. Rendered by the pill and by the mobile search pill.
 *
 * The live region stays mounted and only its contents toggle. A `role="status"`
 * node inserted with its text already in place is commonly missed by screen
 * readers, and this is the only signal that the results no longer match the clock.
 */
function _temp3(e_2) {
  return e_2.preventDefault();
}
function _temp2() {
  return safeUUID();
}
function _temp(s) {
  return s.staleWindow;
}
export function StaleWindowHint(t0) {
  const $ = _c(4);
  const {
    show
  } = t0;
  let t1;
  if ($[0] !== show) {
    t1 = show && <div className="relative w-max max-w-[min(20rem,calc(100vw-2rem))] rounded-(--r-md) border border-(--blue) bg-(--blue-pale) px-3 py-2 shadow-(--shadow-md)"><div className="flex items-start gap-2"><Clock className="mt-0.5 h-3.5 w-3.5 shrink-0 text-accent" aria-hidden={true} /><p className="text-[11px] leading-snug text-accent-dark"><span className="font-semibold">{STALE_WINDOW_MESSAGE.lead}</span>{" "}{STALE_WINDOW_MESSAGE.detail}</p></div><span aria-hidden={true} className="absolute bottom-full left-1/2 -ml-[6px] h-0 w-0 border-x-[6px] border-b-[6px] border-x-transparent border-b-(--blue)" /></div>;
    $[0] = show;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t1) {
    t2 = <div role="status" className="pointer-events-none absolute left-1/2 top-full z-50 mt-2 -translate-x-1/2">{t1}</div>;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  return t2;
}